import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, ChevronRight, Plus, Trash2, Copy, GripVertical, Settings, X, 
  MoreVertical, Type, DollarSign, Image as ImageIcon, List, LayoutList, Layers, GitBranch
} from 'lucide-react';
import { useSortable, sortableKeyboardCoordinates, SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { DragEndEvent, useSensor, useSensors, PointerSensor, KeyboardSensor, DndContext } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { MenuData, MenuItem, ChoiceGroup, LocalizedText, MenuCategory } from '../../types';
import { apiClient } from '../../services/apiClient';
import { ConfirmationModal } from '../ConfirmationModal';

// Helper for Image Resolution
const resolveImageUrl = (raw: string | undefined | null) => {
  if (!raw) return null;
  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('/api/assets')) return raw;
  if (raw.includes('.r2.dev/')) return `/api/assets?key=${encodeURIComponent(raw.split('.r2.dev/')[1])}`;
  if (raw.includes('sterlingops-assets/')) return `/api/assets?key=${encodeURIComponent(raw.split('sterlingops-assets/')[1])}`;
  if (raw.startsWith('uploads/')) return `/api/assets?key=${encodeURIComponent(raw)}`;
  if (raw.startsWith('http')) return raw;
  return `data:image/png;base64,${raw}`;
};

const sanitizeFileName = (itemName?: string | null, fallback = 'item-image') => {
  const base = (itemName || '').trim().toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-') || fallback;
  const suffix = Math.random().toString(36).slice(2, 8);
  return `${base}-${suffix}`;
};

interface MenuEditorProps {
  data: MenuData;
  setData: React.Dispatch<React.SetStateAction<MenuData>>;
  focusTarget?: { id: string; ts: number } | null;
  onCategoryClick?: (id: string) => void;
  currency: string;
  requestConfirmation: (options: { title: string; message: string; onConfirm: () => void; type?: 'confirm' | 'success' | 'danger' }) => void;
  healthFilterKey?: string | null;
  healthIssueLabel?: string;
  healthHighlight?: { categoryIds: Set<string>; itemIds: Set<string> };
  onClearHealthFilter?: () => void;
  onShowToast?: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  onValidityChange?: (valid: boolean) => void;
}

// --- Sub-Components ---

const BilingualInput = ({ 
  label, 
  enVal, 
  arVal, 
  onEnChange, 
  onArChange, 
  multiline = false,
  error,
}: { 
  label: string, 
  enVal: string, 
  arVal: string, 
  onEnChange: (v: string) => void, 
  onArChange: (v: string) => void,
  multiline?: boolean;
  error?: string | null;
}) => (
  <div className="mb-3">
    <div className="flex justify-between items-center mb-1.5">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      <div className="flex gap-2">
        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 rounded">EN</span>
        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 rounded">AR</span>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {multiline ? (
        <>
          <textarea 
            value={enVal} 
            onChange={e => onEnChange(e.target.value)}
            className={`w-full p-2 text-sm border rounded outline-none resize-none h-20 ${error ? 'border-red-300 focus:border-red-400 focus:ring-1 focus:ring-red-200' : 'border-slate-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500'}`}
            placeholder="English..."
            dir="ltr"
          />
          <textarea 
            value={arVal} 
            onChange={e => onArChange(e.target.value)}
            className="w-full p-2 text-sm border border-slate-200 rounded focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none resize-none h-20 text-right font-arabic"
            placeholder="العربية..."
            dir="rtl"
          />
        </>
      ) : (
        <>
          <input 
            type="text" 
            value={enVal} 
            onChange={e => onEnChange(e.target.value)}
            className={`w-full p-2 text-sm border rounded outline-none ${error ? 'border-red-300 focus:border-red-400 focus:ring-1 focus:ring-red-200' : 'border-slate-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500'}`}
            placeholder="English..."
            dir="ltr"
          />
          <input 
            type="text" 
            value={arVal} 
            onChange={e => onArChange(e.target.value)}
            className="w-full p-2 text-sm border border-slate-200 rounded focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-right font-arabic"
            placeholder="العربية..."
            dir="rtl"
          />
        </>
      )}
    </div>
    {error ? <div className="text-[11px] text-red-600 mt-1">{error}</div> : null}
  </div>
);

// --- Sortable Category Card Wrapper ---
const SortableCategoryCard = ({ id, children }: { id: string, children: React.ReactNode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-6">
       {React.cloneElement(children as React.ReactElement, { ...attributes, ...listeners })}
    </div>
  );
};

const SortableGroupCard = ({ id, children }: { id: string; children: React.ReactNode }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="mb-3">
      {children}
    </div>
  );
};

const SortableOptionRow = ({ id, children }: { id: string; children: React.ReactNode }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const CategoryEditorItem = (props: any) => {
    const { 
        cat, expandedCats, toggleCat, updateCat, delCat, 
        addItem, expandedItems, toggleItem, itemTabs, setItemTab, 
        updateItem, delItem, addCG, copiedGroup, setCopiedGroup, 
        pasteCG, updateCG, delCG, addOpt, updateOpt, delOpt,
        onCategoryClick, currency, highlightCategories, highlightItems, invalidIds, reorderGroups, reorderOptions, dragSensors, ...dragHandleProps
    } = props;

    const isExpanded = expandedCats.has(cat.id);
    const isHighlightedCategory = highlightCategories?.has(cat.id);
    const highlightedItemsSet: Set<string> | undefined = highlightItems;
    const items = Array.isArray(cat.items) ? cat.items : [];

    return (
        <div className={`bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden ${isHighlightedCategory ? 'ring-2 ring-amber-400' : ''}`}>
            {/* Category Header */}
            <div 
                id={`editor-cat-${cat.id}`}
                className="flex items-center p-4 bg-slate-900 border-b border-slate-900 cursor-pointer hover:bg-slate-800 transition-colors"
                onClick={() => toggleCat(cat.id)}
            >
                <div {...dragHandleProps} className="mr-3 text-slate-400 cursor-grab active:cursor-grabbing hover:text-white" onClick={e => e.stopPropagation()}>
                    <GripVertical size={20} />
                </div>
                <div className="flex-1">
                    <h3 className="font-bold text-white">{cat.name.en || 'Untitled Category'}</h3>
                    <p className="text-xs text-slate-400">{items.length} items</p>
                </div>
                <div className="flex items-center gap-2">
                    <button 
                        onClick={(e) => { e.stopPropagation(); delCat(cat.id); }}
                        className="p-2 text-slate-400 hover:text-red-400 hover:bg-white/10 rounded transition-colors"
                    >
                        <Trash2 size={18} />
                    </button>
                    {isExpanded ? <ChevronDown size={20} className="text-slate-400" /> : <ChevronRight size={20} className="text-slate-400" />}
                </div>
            </div>

            {/* Category Body */}
            {isExpanded && (
                <div className="p-4 space-y-6">
                    <BilingualInput 
                        label="Category Name"
                        enVal={cat.name.en}
                        arVal={cat.name.ar || ''}
                        onEnChange={(v) => updateCat(cat.id, 'en', v)}
                        onArChange={(v) => updateCat(cat.id, 'ar', v)}
                        error={invalidIds?.categories?.has(cat.id) ? 'Name is required' : undefined}
                    />

                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Items</h4>
                            <button 
                                onClick={() => addItem(cat.id)}
                                className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded transition-colors"
                            >
                                <Plus size={14} /> Add Item
                            </button>
                        </div>

                        {items.map((item: MenuItem) => {
                            const isItemExpanded = expandedItems.has(item.id);
                            const activeTab = itemTabs[item.id] || 'details';
                            const choiceGroups = Array.isArray(item.choiceGroups) ? item.choiceGroups : [];
                            const itemNameMissing = !item.name?.en?.trim();
                            const priceVal = Number(item.price);
                            const priceInvalid = !Number.isFinite(priceVal) || priceVal < 0;

                            return (
                        <div key={item.id} id={`editor-item-${item.id}`} className={`border border-slate-200 rounded-md overflow-hidden ${highlightedItemsSet?.has(item.id) ? 'ring-2 ring-amber-400' : ''}`}>
                                    <div 
                                        className="flex items-center p-3 bg-slate-50/50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors"
                                        onClick={() => toggleItem(item.id)}
                                    >
                                        <div className="mr-3 text-slate-300">
                                            <GripVertical size={16} />
                                        </div>
                                        <div className="flex-1 font-medium text-sm text-slate-800">
                                            {item.name.en || 'New Item'}
                                        </div>
                                        <div className="text-sm font-bold text-slate-600 mr-4">
                                            {item.price.toFixed(2)} {currency}
                                        </div>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); delItem(cat.id, item.id); }}
                                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors mr-1"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                        {isItemExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                                    </div>

                                    {isItemExpanded && (
                                        <div className="p-4 bg-white">
                                            {/* Item Tabs */}
                                            <div className="flex border-b border-slate-200 mb-4">
                                                <button 
                                                    className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${activeTab === 'details' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                                                    onClick={() => setItemTab(item.id, 'details')}
                                                >
                                                    <LayoutList size={16} /> Basic Info
                                                </button>
                                                <button 
                                                    className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${activeTab === 'extras' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                                                    onClick={() => setItemTab(item.id, 'extras')}
                                                >
                                                    <Layers size={16} /> Add-ons & Media
                                                </button>
                                            </div>

                                            {activeTab === 'details' ? (
                                                <div className="space-y-4">
                                                    <BilingualInput 
                                                        label="Item Name"
                                                        enVal={item.name.en}
                                                        arVal={item.name.ar || ''}
                                                        onEnChange={(v) => updateItem(cat.id, item.id, 'name.en', v)}
                                                        onArChange={(v) => updateItem(cat.id, item.id, 'name.ar', v)}
                                                        error={itemNameMissing ? 'Name is required' : undefined}
                                                    />
                                                    <BilingualInput 
                                                        label="Description"
                                                        enVal={item.description.en}
                                                        arVal={item.description.ar || ''}
                                                        onEnChange={(v) => updateItem(cat.id, item.id, 'description.en', v)}
                                                        onArChange={(v) => updateItem(cat.id, item.id, 'description.ar', v)}
                                                        multiline
                                                    />
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Price ({currency})</label>
                                                            <input 
                                                                type="number" 
                                                                value={item.price}
                                                                onChange={(e) => updateItem(cat.id, item.id, 'price', parseFloat(e.target.value) || 0)}
                                                                className={`w-full p-2 text-sm border rounded outline-none ${priceInvalid ? 'border-red-300 focus:border-red-400 focus:ring-1 focus:ring-red-200' : 'border-slate-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500'}`}
                                                                min="0"
                                                                step="0.5"
                                                            />
                                                            {priceInvalid && <div className="text-[11px] text-red-600 mt-1">Price must be a number ≥ 0</div>}
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Image</label>
                                                            <div className="flex items-start gap-3 mb-2">
                                                                {item.image ? (
                                                                    <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200 group shrink-0 bg-slate-100">
                                                                        <img 
                                                                            src={resolveImageUrl(item.image) || ''} 
                                                                            alt="Item" 
                                                                            className="w-full h-full object-cover"
                                                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; const ph = (e.target as HTMLImageElement).nextElementSibling as HTMLElement; if (ph) ph.classList.remove('hidden'); }}
                                                                        />
                                                                        <div className={`absolute inset-0 flex items-center justify-center text-[11px] text-slate-400 ${item.image ? 'hidden' : ''}`}>
                                                                            <ImageIcon size={16} className="mr-1" /> No image
                                                                        </div>
                                                                        <button 
                                                                            onClick={() => updateItem(cat.id, item.id, 'image', '')}
                                                                            className="absolute inset-0 bg-black/50 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                                                                        >
                                                                            <X size={16} />
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <div className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 bg-slate-50 shrink-0">
                                                                        <ImageIcon size={24} />
                                                                    </div>
                                                                )}
                                                                
                                                                <div className="flex-1 min-w-0">
                                                                    <input 
                                                                        type="file" 
                                                                        accept="image/*"
                                                                        className="hidden"
                                                                        id={`file-${item.id}`}
                                                                        onChange={async (e) => {
                                                                            if (e.target.files?.[0]) {
                                                                                const file = e.target.files[0];
                                                                                try {
                                                                                    const extMatch = file.name?.match(/\\.[a-zA-Z0-9]+$/);
                                                                                    const ext = extMatch ? extMatch[0] : '.jpg';
                                                                                    const cleaned = `${sanitizeFileName(item.name?.en, 'item-image')}${ext}`;
                                                                                    const namedFile = new File([file], cleaned, { type: file.type || 'image/jpeg' });
                                                                                    const res = await apiClient.uploads.uploadImage(namedFile);
                                                                                    const url = (res as any).url || (res as any).key;
                                                                                    updateItem(cat.id, item.id, 'image', url);
                                                                                } catch (err) {
                                                                                    console.error(err);
                                                                                    onShowToast?.('error', 'Upload Failed', 'Could not upload image. Please try again.');
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                    <label 
                                                                        htmlFor={`file-${item.id}`}
                                                                        className="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 rounded text-xs font-medium text-slate-700 hover:bg-slate-50 cursor-pointer shadow-sm mb-1"
                                                                    >
                                                                        Choose Image
                                                                    </label>
                                                                    <input 
                                                                        type="text" 
                                                                        value={item.image || ''}
                                                                        onChange={(e) => updateItem(cat.id, item.id, 'image', e.target.value)}
                                                                        className="w-full p-1.5 text-xs border border-slate-200 rounded text-slate-500 focus:border-emerald-500 outline-none"
                                                                        placeholder="Or paste URL..."
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="space-y-6">
                                                    <div className="flex justify-between items-center">
                                                        <h5 className="text-sm font-bold text-slate-700">Choice Groups</h5>
                                                        <div className="flex gap-2">
                                                            {copiedGroup && (
                                                                <button 
                                                                    onClick={() => pasteCG(cat.id, item.id)}
                                                                    className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded"
                                                                >
                                                                    <Copy size={12} /> Paste Group
                                                                </button>
                                                            )}
                                                            <button 
                                                                onClick={() => addCG(cat.id, item.id)}
                                                                className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded"
                                                            >
                                                                <Plus size={12} /> Add Group
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {choiceGroups.length === 0 && (
                                                        <div className="text-center py-6 bg-slate-50 rounded border border-dashed border-slate-200 text-slate-400 text-sm">
                                                            No choice groups added.
                                                        </div>
                                                    )}

                                                    {choiceGroups.length > 0 && (
                                                      <DndContext sensors={dragSensors} onDragEnd={({ active, over }) => {
                                                        if (!over || active.id === over.id) return;
                                                        reorderGroups(cat.id, item.id, active.id as string, over.id as string);
                                                      }}>
                                                        <SortableContext items={choiceGroups.map(g => g.id)} strategy={verticalListSortingStrategy}>
                                                    {choiceGroups.map((group: ChoiceGroup, gIdx) => {
                                                        const options = Array.isArray(group.options) ? group.options : [];
                                                        return (
                                                        <SortableGroupCard key={group.id} id={group.id}>
                                                        <div className="border border-slate-200 rounded bg-slate-50/30 p-3">
                                                            <div className="flex justify-between items-start mb-3">
                                                                <div className="flex-1 grid grid-cols-2 gap-2 mr-4">
                                                                    <input 
                                                                        type="text" 
                                                                        value={group.name.en}
                                                                        onChange={(e) => updateCG(cat.id, item.id, group.id, 'name.en', e.target.value)}
                                                                        className="p-1.5 text-sm border border-slate-200 rounded"
                                                                        placeholder="Group Name (EN)"
                                                                    />
                                                                    <input 
                                                                        type="text" 
                                                                        value={group.name.ar || ''}
                                                                        onChange={(e) => updateCG(cat.id, item.id, group.id, 'name.ar', e.target.value)}
                                                                        className="p-1.5 text-sm border border-slate-200 rounded text-right font-arabic"
                                                                        placeholder="اسم المجموعة (AR)"
                                                                    />
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <button 
                                                                        onClick={() => setCopiedGroup(group)}
                                                                        className="p-1.5 text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 rounded"
                                                                        title="Copy Group"
                                                                    >
                                                                        <Copy size={14} />
                                                                    </button>
                                                                    <button 
                                                                        onClick={() => delCG(cat.id, item.id, group.id)}
                                                                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            <div className="flex gap-4 mb-3 text-xs">
                                                                <label className="flex items-center gap-1.5 cursor-pointer">
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={group.required}
                                                                        onChange={(e) => updateCG(cat.id, item.id, group.id, 'required', e.target.checked)}
                                                                        className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                                                    />
                                                                    <span className="text-slate-600">Required</span>
                                                                </label>
                                                                <label className="flex items-center gap-1.5 cursor-pointer">
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={!group.required}
                                                                        onChange={(e) => updateCG(cat.id, item.id, group.id, 'required', !e.target.checked)}
                                                                        className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                                                    />
                                                                    <span className="text-slate-600">Optional</span>
                                                                </label>
                                                                <div className="flex items-center gap-2 ml-2 border-l pl-4 border-slate-200">
                                                                    <span className="text-slate-500">Min:</span>
                                                                    <input 
                                                                        type="number" 
                                                                        value={group.min}
                                                                        onChange={(e) => updateCG(cat.id, item.id, group.id, 'min', parseInt(e.target.value) || 0)}
                                                                        className="w-12 p-1 border border-slate-200 rounded text-center"
                                                                        min="0"
                                                                    />
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-slate-500">Max:</span>
                                                                    <input 
                                                                        type="number" 
                                                                        value={group.max}
                                                                        onChange={(e) => updateCG(cat.id, item.id, group.id, 'max', parseInt(e.target.value) || 0)}
                                                                        className="w-12 p-1 border border-slate-200 rounded text-center"
                                                                        min="0"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="space-y-2 pl-2 border-l-2 border-slate-200">
                                                                {options.map(opt => (
                                                                    <div key={opt.id} className="flex items-center gap-2">
                                                                        <input 
                                                                            type="text" 
                                                                            value={opt.name.en}
                                                                            onChange={(e) => updateOpt(cat.id, item.id, group.id, opt.id, 'name.en', e.target.value)}
                                                                            className="flex-1 min-w-0 p-1.5 text-sm border border-slate-200 rounded"
                                                                            placeholder="Option (EN)"
                                                                        />
                                                                        <input 
                                                                            type="text" 
                                                                            value={opt.name.ar || ''}
                                                                            onChange={(e) => updateOpt(cat.id, item.id, group.id, opt.id, 'name.ar', e.target.value)}
                                                                            className="flex-1 min-w-0 p-1.5 text-sm border border-slate-200 rounded text-right font-arabic"
                                                                            placeholder="الخيار (AR)"
                                                                        />
                                                                        <div className="relative w-28 shrink-0">
                                                                            <span className="absolute left-2 top-1.5 text-slate-400 text-xs">+</span>
                                                                            <input 
                                                                                type="number" 
                                                                                value={opt.priceModifier || 0}
                                                                                onChange={(e) => updateOpt(cat.id, item.id, group.id, opt.id, 'priceModifier', parseFloat(e.target.value) || 0)}
                                                                                className="w-full p-1.5 pl-5 text-sm border border-slate-200 rounded"
                                                                            />
                                                                        </div>
                                                                        <button 
                                                                            onClick={() => delOpt(cat.id, item.id, group.id, opt.id)}
                                                                            className="p-1.5 text-slate-300 hover:text-red-500 shrink-0"
                                                                        >
                                                                            <X size={14} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                                <button 
                                                                    onClick={() => addOpt(cat.id, item.id, group.id)}
                                                                    className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 mt-2"
                                                                >
                                                                    <Plus size={12} /> Add Option
                                                                </button>
                                                            </div>
                                                        </div>
                                                        </SortableGroupCard>
                                                        );
                                                    })}
                                                        </SortableContext>
                                                      </DndContext>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};


export const MenuEditor: React.FC<MenuEditorProps> = ({ data, setData, focusTarget, onCategoryClick, currency, requestConfirmation, healthFilterKey, healthIssueLabel, healthHighlight, onClearHealthFilter, onShowToast, onValidityChange }) => {
    const categories = Array.isArray(data.categories) ? data.categories : [];
    const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set(categories[0]?.id ? [categories[0].id] : []));
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  
  const [itemTabs, setItemTabs] = useState<Record<string, 'details' | 'extras'>>({});
  const [copiedGroup, setCopiedGroup] = useState<ChoiceGroup | null>(null);
  const [invalidIds, setInvalidIds] = useState<{ categories: Set<string>; items: Set<string>; groups: Set<string>; }>({ categories: new Set(), items: new Set(), groups: new Set() });
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
        activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (focusTarget) {
        setExpandedCats(prev => new Set(prev).add(focusTarget.id));
        // Also expand the category if it's an item
        const cat = categories.find(c => Array.isArray(c.items) && c.items.some(i => i.id === focusTarget.id));
        if (cat) {
            setExpandedCats(prev => new Set(prev).add(cat.id));
            setExpandedItems(prev => new Set(prev).add(focusTarget.id));
        }

        // Scroll into view
        setTimeout(() => {
            const el = document.getElementById(`editor-item-${focusTarget.id}`) || document.getElementById(`editor-cat-${focusTarget.id}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Optional: Add a temporary highlight class
                el.classList.add('ring-2', 'ring-emerald-500');
                setTimeout(() => el.classList.remove('ring-2', 'ring-emerald-500'), 2000);
            }
        }, 100);
    }
    }, [focusTarget, categories]);

  useEffect(() => {
    const badCats = new Set<string>();
    const badItems = new Set<string>();
    const badGroups = new Set<string>();

    categories.forEach((cat) => {
      if (!cat.name?.en?.trim()) badCats.add(cat.id);
      (cat.items || []).forEach((item) => {
        if (!item.name?.en?.trim()) badItems.add(item.id);
        const price = Number(item.price);
        if (!Number.isFinite(price) || price < 0) badItems.add(item.id);
        (item.choiceGroups || []).forEach((group) => {
          if (!group.name?.en?.trim()) badGroups.add(group.id);
          if (group.required && group.min < 1) badGroups.add(group.id);
        });
      });
    });

    setInvalidIds({ categories: badCats, items: badItems, groups: badGroups });
    onValidityChange?.(badCats.size === 0 && badItems.size === 0 && badGroups.size === 0);
  }, [categories, onValidityChange]);

  useEffect(() => {
    const badCats = new Set<string>();
    const badItems = new Set<string>();
    const badGroups = new Set<string>();

    categories.forEach((cat) => {
      const name = (cat.name?.en || '').trim();
      if (!name) badCats.add(cat.id);
      (cat.items || []).forEach((item) => {
        const itemName = (item.name?.en || '').trim();
        if (!itemName) badItems.add(item.id);
        const price = Number(item.price);
        if (!Number.isFinite(price) || price < 0) badItems.add(item.id);
        (item.choiceGroups || []).forEach((group) => {
          const gName = (group.name?.en || '').trim();
          if (!gName) badGroups.add(group.id);
          if (group.required && group.min < 1) badGroups.add(group.id);
        });
      });
    });

    setInvalidIds({ categories: badCats, items: badItems, groups: badGroups });
    onValidityChange?.(badCats.size === 0 && badItems.size === 0 && badGroups.size === 0);
  }, [categories, onValidityChange]);

  // --- CRUD Actions ---
  const toggleCat = (id: string) => {
    const next = new Set(expandedCats);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedCats(next);
  };

  const toggleItem = (id: string) => {
    const next = new Set(expandedItems);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedItems(next);
  };

  const setItemTab = (itemId: string, tab: 'details' | 'extras') => {
      setItemTabs(prev => ({...prev, [itemId]: tab}));
  };

  const updateCat = (id: string, f: 'en' | 'ar', v: string) => 
    setData(p => ({...p, categories: p.categories.map(c => c.id === id ? {...c, name: {...c.name, [f]: v}} : c)}));
  
  const addCat = () => {
    const newCat: MenuCategory = { id: `cat-${Date.now()}`, name: { en: 'New Section', ar: 'قسم جديد' }, items: [] };
    setData(p => ({...p, categories: [...p.categories, newCat]}));
    setExpandedCats(p => new Set(p).add(newCat.id));
  };
  
  const delCat = (id: string) => {
      requestConfirmation({
          title: 'Delete Category?',
          message: 'Are you sure you want to delete this category? All items inside it will be removed.',
          type: 'danger',
          onConfirm: () => {
              setData(p => ({...p, categories: p.categories.filter(c => c.id !== id)}));
          }
      });
  };

  const addItem = (catId: string) => {
    const newItem: MenuItem = { id: `item-${Date.now()}`, name: { en: 'New Dish', ar: 'طبق جديد' }, description: { en: '', ar: '' }, price: 0, choiceGroups: [] };
    setData(p => ({...p, categories: p.categories.map(c => c.id === catId ? {...c, items: [...c.items, newItem]} : c)}));
    setExpandedItems(p => new Set(p).add(newItem.id));
  };

  const updateItem = (catId: string, itemId: string, field: string, val: any) => {
      setData(p => ({...p, categories: p.categories.map(c => c.id === catId ? {
          ...c, 
          items: c.items.map(i => {
              if (i.id !== itemId) return i;
              if (field.includes('.')) {
                  const [parent, child] = field.split('.');
                  return { ...i, [parent]: { ...(i as any)[parent], [child]: val } };
              }
              return { ...i, [field]: val };
          })
      } : c)}));
  };

  const delItem = (catId: string, itemId: string) => {
      requestConfirmation({
          title: 'Delete Item?',
          message: 'Are you sure you want to delete this item? This action cannot be undone.',
          type: 'danger',
          onConfirm: () => {
              setData(p => ({...p, categories: p.categories.map(c => c.id === catId ? {...c, items: c.items.filter(i => i.id !== itemId)} : c)}));
          }
      });
  };

  // Choice Logic
  const addCG = (catId: string, itemId: string) => {
      const newGroup: ChoiceGroup = {
          id: `cg-${Date.now()}`,
          name: { en: 'New Group', ar: 'مجموعة جديدة' },
          options: [],
          required: false,
          optional: true,
          min: 0,
          max: 1
      };
      setData(p => ({...p, categories: p.categories.map(c => c.id === catId ? {
          ...c, items: c.items.map(i => i.id === itemId ? {...i, choiceGroups: [...i.choiceGroups, newGroup]} : i)
      } : c)}));
  };

  const pasteCG = (catId: string, itemId: string) => {
      if (!copiedGroup) return;
      const newGroup = { ...copiedGroup, id: `cg-${Date.now()}`, options: copiedGroup.options.map(o => ({...o, id: `opt-${Date.now()}-${Math.random()}`})) };
      setData(p => ({...p, categories: p.categories.map(c => c.id === catId ? {
          ...c, items: c.items.map(i => i.id === itemId ? {...i, choiceGroups: [...i.choiceGroups, newGroup]} : i)
      } : c)}));
  };

  const updateCG = (catId: string, itemId: string, gId: string, field: string, val: any) => {
      setData(p => ({...p, categories: p.categories.map(c => c.id === catId ? {
          ...c, items: c.items.map(i => i.id === itemId ? {
              ...i, choiceGroups: i.choiceGroups.map(g => {
                  if (g.id !== gId) return g;
                  if (field.includes('.')) {
                      const [parent, child] = field.split('.');
                      return { ...g, [parent]: { ...(g as any)[parent], [child]: val } };
                  }
                  return { ...g, [field]: val };
              })
          } : i)
      } : c)}));
  };

  const delCG = (catId: string, itemId: string, gId: string) => {
      requestConfirmation({
          title: 'Delete Choice Group?',
          message: 'Are you sure you want to delete this choice group and all its options?',
          type: 'danger',
          onConfirm: () => {
              setData(p => ({...p, categories: p.categories.map(c => c.id === catId ? {...c, items: c.items.map(i => i.id === itemId ? {...i, choiceGroups: i.choiceGroups.filter(g => g.id !== gId)} : i)} : c)}));
          }
      });
  };

  const addOpt = (catId: string, itemId: string, gId: string) => {
      const newOpt = { id: `opt-${Date.now()}`, name: { en: 'Option', ar: 'خيار' }, priceModifier: 0 };
      setData(p => ({...p, categories: p.categories.map(c => c.id === catId ? {
          ...c, items: c.items.map(i => i.id === itemId ? {
              ...i, choiceGroups: i.choiceGroups.map(g => g.id === gId ? {...g, options: [...g.options, newOpt]} : g)
          } : i)
      } : c)}));
  };

  const updateOpt = (catId: string, itemId: string, gId: string, oId: string, field: string, val: any) => {
      setData(p => ({...p, categories: p.categories.map(c => c.id === catId ? {
          ...c, items: c.items.map(i => i.id === itemId ? {
              ...i, choiceGroups: i.choiceGroups.map(g => g.id === gId ? {
                  ...g, options: g.options.map(o => {
                      if (o.id !== oId) return o;
                      if (field.includes('.')) {
                          const [parent, child] = field.split('.');
                          return { ...o, [parent]: { ...(o as any)[parent], [child]: val } };
                      }
                      return { ...o, [field]: val };
                  })
              } : g)
          } : i)
      } : c)}));
  };

  const delOpt = (catId: string, itemId: string, gId: string, oId: string) => {
      requestConfirmation({
          title: 'Delete Option?',
          message: 'Are you sure you want to delete this option?',
          type: 'danger',
          onConfirm: () => {
              setData(p => ({...p, categories: p.categories.map(c => c.id === catId ? {...c, items: c.items.map(i => i.id === itemId ? 
                {...i, choiceGroups: i.choiceGroups.map(g => g.id === gId ? {...g, options: g.options.filter(o => o.id !== oId)} : g)} : i)} : c)}));
          }
      });
  };

  const reorderGroups = (catId: string, itemId: string, activeId: string, overId: string) => {
      setData(p => ({ ...p, categories: p.categories.map(c => {
        if (c.id !== catId) return c;
        return {
          ...c,
          items: c.items.map(i => {
            if (i.id !== itemId) return i;
            const groups = i.choiceGroups || [];
            const from = groups.findIndex(g => g.id === activeId);
            const to = groups.findIndex(g => g.id === overId);
            if (from < 0 || to < 0) return i;
            const reordered = arrayMove(groups, from, to).map((g, idx) => ({ ...g, sortIndex: idx }));
            return { ...i, choiceGroups: reordered };
          })
        };
      })}));
  };

  const reorderOptions = (catId: string, itemId: string, groupId: string, activeId: string, overId: string) => {
      setData(p => ({ ...p, categories: p.categories.map(c => {
        if (c.id !== catId) return c;
        return {
          ...c,
          items: c.items.map(i => {
            if (i.id !== itemId) return i;
            return {
              ...i,
              choiceGroups: i.choiceGroups.map(g => {
                if (g.id !== groupId) return g;
                const opts = g.options || [];
                const from = opts.findIndex(o => o.id === activeId);
                const to = opts.findIndex(o => o.id === overId);
                if (from < 0 || to < 0) return g;
                const reordered = arrayMove(opts, from, to).map((o, idx) => ({ ...o, sortIndex: idx }));
                return { ...g, options: reordered };
              })
            };
          })
        };
      })}));
  };


  return (
    <div className="h-full flex flex-col bg-white border-r border-slate-200 text-sm">
        <div className="p-5 border-b border-slate-200 bg-white">
            <div className="flex items-center gap-2 mb-1">
                <GitBranch className="text-emerald-600" size={24} />
                <h2 className="text-xl font-bold text-slate-900">Menu Editor</h2>
            </div>
            <p className="text-sm text-slate-500 mb-6">Configure your menu content and structure.</p>
            
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <BilingualInput 
                    label="Menu Title"
                    enVal={data.title.en}
                    arVal={data.title.ar || ''}
                    onEnChange={(v) => setData(p => ({...p, title: {...p.title, en: v}}))}
                    onArChange={(v) => setData(p => ({...p, title: {...p.title, ar: v}}))}
                />
            </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {healthFilterKey && healthIssueLabel && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 flex items-center justify-between">
                    <div className="text-sm font-semibold">Health filter: {healthIssueLabel}</div>
                    <button
                        onClick={onClearHealthFilter}
                        className="text-xs font-bold px-3 py-1.5 rounded-md border border-amber-200 hover:bg-amber-100"
                    >
                        Clear
                    </button>
                </div>
            )}
            {categories.map(cat => (
                <SortableCategoryCard key={cat.id} id={cat.id}>
                    <CategoryEditorItem 
                        cat={cat}
                        expandedCats={expandedCats}
                        toggleCat={toggleCat}
                        updateCat={updateCat}
                        delCat={delCat}
                        addItem={addItem}
                        expandedItems={expandedItems}
                        toggleItem={toggleItem}
                        itemTabs={itemTabs}
                        setItemTab={setItemTab}
                        updateItem={updateItem}
                        delItem={delItem}
                        addCG={addCG}
                        copiedGroup={copiedGroup}
                        setCopiedGroup={setCopiedGroup}
                        pasteCG={pasteCG}
                        updateCG={updateCG}
                        delCG={delCG}
                        addOpt={addOpt}
                        updateOpt={updateOpt}
                        delOpt={delOpt}
                        onCategoryClick={onCategoryClick}
                        currency={currency}
                        highlightCategories={healthHighlight?.categoryIds}
                        highlightItems={healthHighlight?.itemIds}
                        invalidIds={invalidIds}
                        reorderGroups={reorderGroups}
                        reorderOptions={reorderOptions}
                        dragSensors={sensors}
                    />
                </SortableCategoryCard>
            ))}

            <button 
                onClick={addCat}
                className="w-full py-3 bg-emerald-600 text-white rounded-lg font-bold hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 shadow-sm"
            >
                <Plus size={20} /> New Category
            </button>
        </div>
    </div>
  );
};
